<template>
  <div>
    <Head :title="title" />

    <div class="eu-wrap">
      <form @submit.prevent="update">

        <!-- Top: Avatar + Name + Badges -->
        <div class="eu-top">
          <div class="eu-avatar">
            <img v-if="user.photo_path" :src="user.photo_path" class="w-full h-full object-cover" />
            <span v-else class="eu-initials">{{ (user.first_name || '?')[0].toUpperCase() }}</span>
          </div>
          <div class="eu-top-info">
            <div class="eu-top-name">{{ user.first_name }} {{ user.last_name }}</div>
            <div class="eu-top-meta">
              <span class="eu-top-email">{{ user.email }}</span>
              <span v-if="user.role" class="eu-badge eu-badge--green">{{ user.role.name }}</span>
              <span v-if="user.deleted_at" class="eu-badge eu-badge--red">Deleted</span>
            </div>
            <div v-if="auth.user.role.slug === 'super_admin'" class="eu-top-last-login">
              {{ user.last_login ? 'Last login ' + moment(user.last_login).fromNow() : 'Never logged in' }}
            </div>
          </div>
          <div class="eu-top-toggles">
            <button type="button" @click="form.is_active = form.is_active ? 0 : 1"
              :class="['eu-toggle', form.is_active ? 'eu-toggle--on' : 'eu-toggle--off']">
              <span class="eu-toggle-track"><span class="eu-toggle-knob"></span></span>
              <span>{{ form.is_active ? 'Active' : 'Inactive' }}</span>
            </button>
            <button type="button" @click="form.is_assign = form.is_assign ? 0 : 1"
              :class="['eu-toggle', form.is_assign ? 'eu-toggle--on' : 'eu-toggle--off']">
              <span class="eu-toggle-track"><span class="eu-toggle-knob"></span></span>
              <span>{{ form.is_assign ? 'Assignable' : 'Not Assignable' }}</span>
            </button>
          </div>
        </div>

        <!-- Form Body -->
        <div class="eu-body">

          <!-- Section: Personal -->
          <p class="eu-section-label">Personal</p>
          <div class="eu-grid eu-grid--3">
            <text-input v-model="form.first_name" :error="form.errors.first_name" :label="__('First name')" />
            <text-input v-model="form.last_name"  :error="form.errors.last_name"  :label="__('Last name')" />
            <text-input v-model="form.email"       :error="form.errors.email"       :label="__('Email')" />
            <text-input v-model="form.phone"       :error="form.errors.phone"       :label="__('Phone')" />
            <text-input v-model="form.city"        :error="form.errors.city"        :label="__('City')" />
            <text-input v-model="form.address"     :error="form.errors.address"     :label="__('Address')" />
            <select-input v-model="form.country_id" :error="form.errors.country_id" :label="__('Country')">
              <option :value="null" />
              <option v-for="c in countries" :key="c.id" :value="c.id">{{ __(c.name) }}</option>
            </select-input>
          </div>

          <div class="eu-sep"></div>

          <!-- Section: Account -->
          <p class="eu-section-label">Account</p>
          <div class="eu-grid eu-grid--4">
            <select-input v-model="form.department_id" :error="form.errors.department_id" :label="__('Department')">
              <option :value="null" />
              <option v-for="c in departments" :key="c.id" :value="c.id">{{ __(c.name) }}</option>
            </select-input>
            <select-input v-model="form.channel_id" :error="form.errors.channel_id" :label="__('Channel')">
              <option :value="null" />
              <option v-for="c in channels" :key="c.id" :value="c.id">{{ __(c.name) }}</option>
            </select-input>
            <select-input v-if="user.id !== auth.user.id || ['admin', 'super_admin'].includes(auth.user.role.slug)" v-model="form.role_id" :error="form.errors.role" :label="__('Role')">
              <option :value="null" />
              <option v-for="c in roles" :key="c.id" :value="c.id">{{ __(c.name) }}</option>
            </select-input>
            <text-input v-model="form.password" :error="form.errors.password" type="password" autocomplete="new-password" :label="__('New Password')" />
            <!-- Photo picker -->
            <div class="eu-photo-cell">
              <label class="form-label">Photo</label>
              <input ref="photoInput" type="file" accept="image/*" class="hidden" @change="onPhotoChange" />
              <div class="eu-photo-picker" @click="$refs.photoInput.click()" title="Click to change photo">
                <img v-if="photoPreview || user.photo_path" :src="photoPreview || user.photo_path" class="eu-photo-picker-img" />
                <div v-else class="eu-photo-picker-initials">{{ (user.first_name || '?')[0].toUpperCase() }}</div>
                <div class="eu-photo-picker-overlay">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="eu-photo-cam-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                  </svg>
                </div>
              </div>
              <div v-if="form.photo" class="eu-photo-info">
                <span class="eu-photo-fname">{{ form.photo.name }}</span>
                <button type="button" class="eu-photo-clear" @click="removePhoto" title="Remove">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3 h-3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div v-if="form.errors.photo" class="form-error">{{ form.errors.photo }}</div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="eu-footer">
          <button
            v-if="user.id !== auth.user.id && user_access.user.delete && !user.deleted_at"
            type="button" class="eu-btn-delete" @click="destroy">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            {{ __('Delete User') }}
          </button>
          <button v-if="user.deleted_at" type="button" class="eu-btn-restore" @click="restore">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            {{ __('Restore User') }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ __('Update User') }}
          </loading-button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import moment from 'moment'

export default {
  components: { Head, Link, LoadingButton, SelectInput, TextInput },
  layout: Layout,
  props: {
    user: Object, auth: Object, countries: Array,
    departments: Array, channels: Array,
    status: Array, roles: Array, cities: Array, title: String,
  },
  data() {
    return {
      user_access: this.$page.props.auth.user.access,
      photoPreview: null,
      form: this.$inertia.form({
        _method:       'put',
        first_name:    this.user.first_name,
        last_name:     this.user.last_name,
        email:         this.user.email,
        phone:         this.user.phone,
        department_id: this.user.department_id,
        channel_id:    this.user.channel_id,
        city:          this.user.city,
        address:       this.user.address,
        country_id:    this.user.country_id,
        password:      '',
        role_id:       this.user.role_id,
        is_active:     this.user.is_active ? 1 : 0,
        is_assign:     this.user.is_assign ? 1 : 0,
        photo:         null,
      }),
    }
  },
  methods: {
    update() {
      this.form.post(this.route('users.update', this.user.id), {
        onSuccess: () => { this.form.reset('password', 'photo'); this.photoPreview = null },
      })
    },
    onPhotoChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.form.photo = file
      this.photoPreview = URL.createObjectURL(file)
    },
    removePhoto() {
      this.form.photo = null
      this.photoPreview = null
      this.$refs.photoInput.value = ''
    },
    destroy() {
      if (confirm('Are you sure you want to delete this user?')) {
        this.$inertia.delete(route('users.destroy', this.user.id))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this user?')) {
        this.$inertia.put(route('users.restore', this.user.id))
      }
    },
  },
}
</script>

